// Shared attachment upload primitive: reserve a presigned PUT from our API, then upload the bytes
// directly to object storage (bytes never transit our containers). Returns the attachment id.
//
// Pass threadId for a reply within an existing thread, or null/undefined for a "request" attachment
// on the initial car request (which has no thread yet and fans out to multiple shops).
//
// Privacy (instant, client-side only - no server processing): photos are re-encoded through a
// <canvas> here BEFORE upload, which produces a fresh image with no EXIF, so GPS/location is dropped
// and never leaves the device. JPEG/PNG/WebP are re-encoded to the same format; HEIC/HEIF are
// re-encoded to JPEG (strips GPS and makes the photo viewable in browsers that can't render HEIC),
// where the browser can decode them. Anything we can't strip instantly (other types, or images the
// browser can't decode) is uploaded unchanged - so there is no waiting on a background job.

import { API_BASE } from "./apiBase";

const SAME_FORMAT_IMAGE = new Set(["image/jpeg", "image/png", "image/webp"]);
const HEIC_IMAGE = new Set(["image/heic", "image/heif"]);

function reencodeToStrip(file, outputType, outputName) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx || !canvas.width || !canvas.height) {
          reject(new Error("canvas unavailable"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        const quality = outputType === "image/jpeg" || outputType === "image/webp" ? 0.92 : undefined;
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("toBlob failed"));
              return;
            }
            resolve(new File([blob], outputName, { type: blob.type || outputType }));
          },
          outputType,
          quality
        );
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("image decode failed"));
    };
    img.src = url;
  });
}

function withExtension(name, ext) {
  if (!name) return `image.${ext}`;
  const dot = name.lastIndexOf(".");
  return `${dot > 0 ? name.slice(0, dot) : name}.${ext}`;
}

async function prepareUpload(file) {
  const type = (file.type || "").toLowerCase();
  try {
    if (SAME_FORMAT_IMAGE.has(type)) {
      return await reencodeToStrip(file, type, file.name);
    }
    if (HEIC_IMAGE.has(type)) {
      return await reencodeToStrip(file, "image/jpeg", withExtension(file.name, "jpg"));
    }
  } catch {
    // Decode/encode failed (e.g. the browser can't read HEIC) - upload the original unchanged.
  }
  return file;
}

export async function uploadAttachment(
  file,
  { token, apiBase = API_BASE, threadId = null, onProgress, signal } = {}
) {
  const upload = await prepareUpload(file);

  const presignRes = await fetch(`${apiBase}/messages/attachments/presign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      thread_id: threadId || null,
      content_type: upload.type,
      size_bytes: upload.size,
      original_filename: upload.name,
    }),
    signal,
  });

  if (!presignRes.ok) {
    let detail;
    try {
      detail = (await presignRes.json())?.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail || `Could not start upload (${presignRes.status})`);
  }

  const { attachment_id, upload_url, headers: putHeaders } = await presignRes.json();

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", upload_url);
    Object.entries(putHeaders || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Upload failed. Please try again."));
    if (signal) {
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(upload);
  });

  return attachment_id;
}
